import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { IInvokeCopyJobActivity, InvokeCopyJobActivity, InvokeCopyJobLinkedService, ModelConstants, ReferenceAdapter } from 'shell-models-lib';
import { ValueAdapter } from '../value.adapter';
import { IInvokeCopyJobActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { getFabricLsForParameterizedConnections } from '../utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(InvokeCopyJobActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            copyJobId: '*',
            workspaceId: '*',
            operationType: '*',
            failOnEmptyOutput: '*'
        }
    }
})
export class InvokeCopyJobActivityAdapter extends ActivityAsyncAdapter<InvokeCopyJobActivity, IInvokeCopyJobActivity, IInvokeCopyJobActivityContext> {
    public override async toFabricCopilotContext(activity: InvokeCopyJobActivity): Promise<IInvokeCopyJobActivityContext> {
        if (activity) {
            const activityContext: IInvokeCopyJobActivityContext = await super.toFabricCopilotContext(activity);
            activityContext.CopyJobId = ValueAdapter.toResource(activity, InvokeCopyJobActivity.COPYJOBID, true);
            activityContext.WorkspaceId = ValueAdapter.toResource(activity, InvokeCopyJobActivity.WORKSPACEID, true);
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IInvokeCopyJobActivityContext,
        model?: InvokeCopyJobActivity
    ): Promise<InvokeCopyJobActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            ValueAdapter.toModel(model, InvokeCopyJobActivity.COPYJOBID, context.CopyJobId);
            ValueAdapter.toModel(model, InvokeCopyJobActivity.WORKSPACEID, context.WorkspaceId);
        }
        return model;
    }

    protected async _toResource(activity: InvokeCopyJobActivity): Promise<IInvokeCopyJobActivity> {
        const modelCopyJobActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(InvokeCopyJobActivity).name) as IInvokeCopyJobActivity;

        modelCopyJobActivity.typeProperties = {
            copyJobId: ValueAdapter.toResource(activity, InvokeCopyJobActivity.COPYJOBID),
            workspaceId: ValueAdapter.toResource(activity, InvokeCopyJobActivity.WORKSPACEID),
            operationType: 'RunCopyJob',
            failOnEmptyOutput: ValueAdapter.toResource(activity, InvokeCopyJobActivity.FAILONEMPTYOUTPUT)
        };

        modelCopyJobActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.invokeCopyJobLinkedService,
            activity.linkedServiceParameters);

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelCopyJobActivity, parsed);
        }

        return modelCopyJobActivity;
    }

    protected async _toModel(modelActivity: IInvokeCopyJobActivity): Promise<InvokeCopyJobActivity> {
        const activity: InvokeCopyJobActivity = InvokeCopyJobActivity.MODEL.create<InvokeCopyJobActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        ValueAdapter.toModel(activity, InvokeCopyJobActivity.COPYJOBID, modelActivity.typeProperties.copyJobId);
        ValueAdapter.toModel(activity, InvokeCopyJobActivity.WORKSPACEID, modelActivity.typeProperties.workspaceId);
        ValueAdapter.toModel(activity, InvokeCopyJobActivity.FAILONEMPTYOUTPUT, modelActivity.typeProperties.failOnEmptyOutput);
        
        if (modelActivity.linkedServiceName !== undefined) {
            activity.invokeCopyJobLinkedService = getFabricLsForParameterizedConnections(
                modelActivity.linkedServiceName,
                ModelConstants.invokeCopyJobLinkedService,
                activity);

            if (!activity.invokeCopyJobLinkedService) {
                activity.invokeCopyJobLinkedService = ReferenceAdapter.toProxyModel<InvokeCopyJobLinkedService>(Metamodel.modelFor(InvokeCopyJobLinkedService),
                    modelActivity.linkedServiceName,
                    this.context);
            }
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, InvokeCopyJobActivity.MODEL));
        return activity;
    }
}
